import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { Eye, Loader2, Save } from 'lucide-react';

import { Button, ConfirmDialog, Form, Skeleton, Switch, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { dashboardT } from '../../../../../lib/messages';
import { NetworkTemplateAssetDialog } from '../NetworkTemplateAssetDialog/NetworkTemplateAssetDialog';
import { NetworkTemplateConfigurationPanel } from '../NetworkTemplateConfigurationPanel/NetworkTemplateConfigurationPanel';
import { NetworkTemplateLivePreviewDialog } from '../NetworkTemplateLivePreviewDialog/NetworkTemplateLivePreviewDialog';
import { NetworkTemplatePreview } from '../NetworkTemplatePreview/NetworkTemplatePreview';
import { useA2vTemplateMutations, useA2vTemplateQuery } from '../../hooks';
import {
  A2vAssetSelectionKind,
  A2vOverlayPosition,
  A2vTemplateType,
  configurationHasTemplateAssets,
  configurationHasValidOverlays,
  createLivePreviewRequest,
  isValidHttpUrl,
  type A2vConfiguration,
} from '../../model';
import {
  mergeNetworkTemplateFormValue,
  networkTemplateFormSchema,
  toNetworkTemplateFormValue,
  type NetworkTemplateFormValue,
} from '../../networkTemplateFormSchema';

export function NetworkTemplatePage({ publisherId }: { publisherId?: string } = {}) {
  'use no memo';
  const query = useA2vTemplateQuery(publisherId);
  const mutations = useA2vTemplateMutations(publisherId);
  const { can } = useShigoPermissions();
  const canSave = can(
    publisherId ? ShigoPermission.SupplyPublishersA2vSave : ShigoPermission.A2vNetworkTemplateSave,
  );
  const canUpload = can(ShigoPermission.A2vAssetsUpload);
  const [original, setOriginal] = React.useState<A2vConfiguration | null>(null);
  const [assetSelection, setAssetSelection] = React.useState<A2vAssetSelectionKind | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [livePreviewOpen, setLivePreviewOpen] = React.useState(false);
  const [qrCodeAsset, setQrCodeAsset] = React.useState<string | null>(null);
  const [qrCodeSource, setQrCodeSource] = React.useState('');
  const qrGenerationTimeout = React.useRef<number | undefined>(undefined);
  const qrRequestSequence = React.useRef(0);
  const initializedId = React.useRef<string | null>(null);
  const form = useForm<NetworkTemplateFormValue>({
    resolver: zodResolver(networkTemplateFormSchema),
    defaultValues: {
      useCustomTemplate: false,
      templateType: A2vTemplateType.Image,
      qrCodeEnabled: false,
      qrCodeLink: '',
      qrCodePosition: A2vOverlayPosition.BottomRight,
      logoEnabled: false,
      logo: undefined,
      logoPosition: A2vOverlayPosition.TopLeft,
      images: [],
      video: undefined,
    },
  });
  const value = useWatch({ control: form.control });
  const useCustomTemplate = value.useCustomTemplate ?? false;
  const configuration = original
    ? mergeNetworkTemplateFormValue(original, value as NetworkTemplateFormValue)
    : null;
  const interactionPending = mutations.update.isPending || mutations.createLivePreview.isPending;
  const savePending = interactionPending || mutations.generateQrCode.isPending;

  async function generateQrCode(text: string, notify = true) {
    if (!isValidHttpUrl(text)) {
      form.setError('qrCodeLink', {
        message: dashboardT('a2v.networkTemplate.validation.qrUrl'),
      });
      return;
    }
    const normalizedText = text.trim();
    const requestSequence = ++qrRequestSequence.current;
    try {
      const response = await mutations.generateQrCode.mutateAsync(normalizedText);
      if (requestSequence !== qrRequestSequence.current) return;
      setQrCodeAsset(response);
      setQrCodeSource(normalizedText);
      form.clearErrors('qrCodeLink');
    } catch (error) {
      if (notify && requestSequence === qrRequestSequence.current) {
        showRequestError(error, dashboardT('a2v.networkTemplate.qrError'));
      }
    }
  }

  const generateQrCodeEvent = React.useEffectEvent(generateQrCode);

  function invalidateQrGeneration() {
    window.clearTimeout(qrGenerationTimeout.current);
    qrRequestSequence.current += 1;
    setQrCodeAsset(null);
    setQrCodeSource('');
  }

  function scheduleQrGeneration(text: string, delay: number) {
    invalidateQrGeneration();
    if (!isValidHttpUrl(text)) return;
    // Runs from event handlers, so it calls `generateQrCode` directly; the Effect Event is for Effects only.
    qrGenerationTimeout.current = window.setTimeout(() => {
      void generateQrCode(text, false);
    }, delay);
  }

  React.useEffect(() => {
    const loaded = query.data?.a2vConfiguration;
    if (!loaded) return;
    const identity = JSON.stringify([
      publisherId,
      query.data?.useCustomTemplate,
      loaded.templateType,
      loaded.logo?.id,
      loaded.video?.id,
      loaded.images.map(({ id }) => id),
      loaded.qrCodeLink,
    ]);
    if (initializedId.current === identity || form.formState.isDirty) return;
    initializedId.current = identity;
    setOriginal(loaded);
    form.reset(toNetworkTemplateFormValue(loaded, query.data?.useCustomTemplate ?? false));
    if (loaded.qrCodeEnabled && isValidHttpUrl(loaded.qrCodeLink)) {
      qrGenerationTimeout.current = window.setTimeout(() => {
        void generateQrCodeEvent(loaded.qrCodeLink ?? '', false);
      }, 0);
    }
  }, [form, publisherId, query.data]);

  React.useEffect(() => {
    return () => {
      window.clearTimeout(qrGenerationTimeout.current);
      qrRequestSequence.current += 1;
      initializedId.current = null;
    };
  }, []);

  async function saveConfiguration() {
    if (!canSave || !configuration) return;
    try {
      const saved = await mutations.update.mutateAsync({
        configuration,
        useCustomTemplate,
        publisher: query.data?.publisher,
      });
      setOriginal(saved);
      form.reset(toNetworkTemplateFormValue(saved, useCustomTemplate));
      setConfirmOpen(false);
      toast.success(dashboardT('a2v.networkTemplate.saveSuccess'));
    } catch (error) {
      showRequestError(error, dashboardT('a2v.networkTemplate.saveError'));
    }
  }

  async function openLivePreview() {
    if (!configuration) return;
    setLivePreviewOpen(true);
    try {
      await mutations.createLivePreview.mutateAsync(
        createLivePreviewRequest(configuration, qrCodeAsset, sampleAudioUrl(mutations.cdnUrl)),
      );
    } catch (error) {
      showRequestError(error, dashboardT('a2v.networkTemplate.livePreviewError'));
    }
  }

  if (query.isLoading || (publisherId && query.isFetching)) {
    return <NetworkTemplateLoading publisherScoped={Boolean(publisherId)} />;
  }
  if (query.isError || !configuration) {
    return (
      <main
        className={
          publisherId
            ? 'flex min-h-[420px] flex-1 items-center justify-center'
            : 'flex min-h-[420px] flex-1 items-center justify-center p-6'
        }
      >
        <div className="space-y-3 text-center">
          <p className="text-muted-foreground text-sm">
            {dashboardT('a2v.networkTemplate.loadError')}
          </p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {dashboardT('a2v.networkTemplate.retry')}
          </Button>
        </div>
      </main>
    );
  }

  const livePreviewReady =
    configurationHasTemplateAssets(configuration) &&
    configurationHasValidOverlays(configuration, qrCodeAsset) &&
    (!configuration.qrCodeEnabled || qrCodeSource === configuration.qrCodeLink?.trim());

  return (
    <Form {...form}>
      <main
        className={
          publisherId
            ? 'min-w-0 flex-1 overflow-x-hidden'
            : 'page-gutter min-w-0 flex-1 overflow-x-hidden py-4 sm:py-6 lg:py-8'
        }
      >
        <form
          className="mx-auto max-w-[1920px] space-y-5"
          noValidate
          onSubmit={(event) => {
            if (!canSave) {
              event.preventDefault();
              return;
            }
            void form.handleSubmit(() => {
              setConfirmOpen(true);
            })(event);
          }}
        >
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-2">
              <h1 className="text-[26px] font-semibold">
                {dashboardT(
                  publisherId ? 'publisherDetails.a2v.title' : 'a2v.networkTemplate.title',
                )}
              </h1>
              {publisherId ? (
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={useCustomTemplate}
                    disabled={!canSave || mutations.update.isPending}
                    onCheckedChange={(checked) => {
                      form.setValue('useCustomTemplate', checked, { shouldDirty: true });
                    }}
                  />
                  {dashboardT('publisherDetails.a2v.useCustomTemplate')}
                </label>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!livePreviewReady || mutations.createLivePreview.isPending}
                onClick={() => void openLivePreview()}
              >
                {mutations.createLivePreview.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
                {dashboardT('a2v.networkTemplate.livePreview')}
              </Button>
              {canSave ? (
                <Button type="submit" disabled={savePending}>
                  <Save className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('a2v.networkTemplate.save')}
                </Button>
              ) : null}
            </div>
          </header>
          {publisherId && !useCustomTemplate ? (
            <div className="border-border bg-card rounded-md border p-6 text-sm">
              {dashboardT('publisherDetails.a2v.networkTemplateActive')}
            </div>
          ) : (
            <div className="grid items-start gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
              <NetworkTemplateConfigurationPanel
                configuration={configuration}
                canSave={canSave}
                pending={mutations.update.isPending}
                qrGenerating={mutations.generateQrCode.isPending}
                onChooseLogo={() => {
                  setAssetSelection(A2vAssetSelectionKind.Logo);
                }}
                onQrValueChange={(text) => {
                  scheduleQrGeneration(text, 400);
                }}
                onQrEnabledChange={(enabled) => {
                  if (enabled) {
                    scheduleQrGeneration(form.getValues('qrCodeLink'), 0);
                  } else {
                    invalidateQrGeneration();
                  }
                }}
                onGenerateQr={(text) => {
                  window.clearTimeout(qrGenerationTimeout.current);
                  void generateQrCode(text);
                }}
              />
              <NetworkTemplatePreview
                configuration={configuration}
                qrCodeAsset={qrCodeAsset}
                disabled={!canSave || interactionPending}
                onChooseAssets={() => {
                  setAssetSelection(
                    configuration.templateType === A2vTemplateType.Video
                      ? A2vAssetSelectionKind.Video
                      : A2vAssetSelectionKind.Images,
                  );
                }}
                onRemoveAsset={(asset) => {
                  form.setValue(
                    'images',
                    configuration.images.filter(({ id }) => id !== asset.id),
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
              />
            </div>
          )}
        </form>
      </main>
      {assetSelection ? (
        <NetworkTemplateAssetDialog
          kind={assetSelection}
          selected={selectedAssetsForKind(configuration, assetSelection)}
          disabledIds={disabledAssetIdsForKind(configuration, assetSelection)}
          canUpload={canUpload}
          onClose={() => {
            setAssetSelection(null);
          }}
          onApply={(assets) => {
            if (assetSelection === A2vAssetSelectionKind.Logo) {
              form.setValue('logo', assets[0], { shouldDirty: true, shouldValidate: true });
            } else if (assetSelection === A2vAssetSelectionKind.Video) {
              form.setValue('video', assets[0], { shouldDirty: true, shouldValidate: true });
            } else {
              form.setValue('images', assets, { shouldDirty: true, shouldValidate: true });
            }
            setAssetSelection(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={canSave ? confirmOpen : false}
        onOpenChange={(open) => {
          if (canSave) setConfirmOpen(open);
        }}
        title={dashboardT('a2v.networkTemplate.confirmTitle')}
        description={dashboardT('a2v.networkTemplate.confirmDescription')}
        cancelLabel={dashboardT('a2v.networkTemplate.cancel')}
        confirmLabel={dashboardT('a2v.networkTemplate.confirm')}
        pending={mutations.update.isPending}
        onConfirm={() => {
          if (!canSave) return;
          void saveConfiguration();
        }}
      />
      {livePreviewOpen ? (
        <NetworkTemplateLivePreviewDialog
          loading={mutations.createLivePreview.isPending}
          error={mutations.createLivePreview.isError}
          response={mutations.createLivePreview.data}
          cdnUrl={mutations.cdnUrl}
          onClose={() => {
            setLivePreviewOpen(false);
            mutations.createLivePreview.reset();
          }}
        />
      ) : null}
    </Form>
  );
}

function NetworkTemplateLoading({ publisherScoped }: { publisherScoped: boolean }) {
  return (
    <main
      className={
        publisherScoped ? 'min-w-0 flex-1' : 'page-gutter min-w-0 flex-1 py-4 sm:py-6 lg:py-8'
      }
      role="status"
      aria-label={dashboardT('a2v.networkTemplate.loading')}
    >
      <div className="mx-auto max-w-[1920px] space-y-5">
        <div className="flex justify-between">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="grid gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
          <Skeleton className="h-[650px] w-full" />
          <Skeleton className="aspect-video min-h-[420px] w-full" />
        </div>
      </div>
    </main>
  );
}

function selectedAssetsForKind(configuration: A2vConfiguration, kind: A2vAssetSelectionKind) {
  if (kind === A2vAssetSelectionKind.Logo) return configuration.logo ? [configuration.logo] : [];
  if (kind === A2vAssetSelectionKind.Video) {
    return configuration.video ? [configuration.video] : [];
  }
  return configuration.images;
}

function disabledAssetIdsForKind(configuration: A2vConfiguration, kind: A2vAssetSelectionKind) {
  if (kind === A2vAssetSelectionKind.Logo) {
    return configuration.templateType === A2vTemplateType.Video
      ? []
      : configuration.images.map(({ id }) => id);
  }
  return configuration.logo ? [configuration.logo.id] : [];
}

function sampleAudioUrl(cdnUrl: string) {
  const base = cdnUrl.endsWith('/') ? cdnUrl : `${cdnUrl}/`;
  return new URL('a2v_assets/Kulture_Media/positive-cartoon-loop-109009.mp3', base).toString();
}
